import { definePreset } from '@primeng/themes';
import Aura from '@primeng/themes/aura';

export interface ThemePalette {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
}

export interface ThemeConfig {
  primary?: Partial<ThemePalette>;
  secondary?: Partial<ThemePalette>;
  font?: {
    family?: string;
    size?: string;
    weight?: string;
  };
}

const DEFAULT_PRIMARY: ThemePalette = {
  50: '#f8f4e8',
  100: '#f1e9d1',
  200: '#e3d3a4',
  300: '#d6bd77',
  400: '#cba660',
  500: '#b38e50',
  600: '#8d6f3e',
  700: '#66512c',
  800: '#40321a',
  900: '#1a1408',
};

const DEFAULT_SECONDARY: ThemePalette = {
  50: '#eaf6f8',
  100: '#cdeff2',
  200: '#99dfea',
  300: '#66cfe2',
  400: '#333333',
  500: '#2b9eb3',
  600: '#217d8e',
  700: '#175c68',
  800: '#0d3b43',
  900: '#051b1d',
};

const DEFAULT_FONT = {
  family: 'Satoshi, sans-serif',
  size: '14px',
  weight: 'normal',
};

export function createPreset(config: ThemeConfig = {}) {
  const primary = { ...DEFAULT_PRIMARY, ...config.primary };
  const secondary = { ...DEFAULT_SECONDARY, ...config.secondary };
  const font = { ...DEFAULT_FONT, ...config.font };

  return definePreset(Aura, {
    semantic: {
      primary,
    },
    colorScheme: {
      light: {
        primary: {
          color: '{primary.400}',
          contrastColor: '#ffffff',
          hoverColor: '{primary.800}',
          activeColor: '{primary.700}',
        },
        highlight: {
          background: '{primary.500}',
          focusBackground: '{primary.700}',
          color: '#ffffff',
          focusColor: '#ffffff',
        },
      },
      dark: {
        primary: {
          color: '{primary.400}',
          contrastColor: '#ffffff',
          hoverColor: '{primary.500}',
          activeColor: '{primary.600}',
        },
        highlight: {
          background: '{primary.400}',
          focusBackground: '{primary.500}',
          color: '#ffffff',
          focusColor: '#ffffff',
        },
      },
      font,
    },
  });
}

/** Default preset matching the cropmap-19 dark theme */
const DkPreset = createPreset();
export default DkPreset;

/**
 * Apply a runtime theme by updating CSS custom properties on `<body>`.
 * Pass partial overrides — only the tokens you provide will be updated.
 *
 * @example
 * applyTheme({ '--dk-primary': '#e74c3c', '--dk-bg-body': '#111' });
 */
export function applyTheme(tokens: Record<string, string>, target: HTMLElement = document.body) {
  Object.entries(tokens).forEach(([prop, value]) => {
    target.style.setProperty(prop, value);
  });
}

/**
 * Switch between named data-theme attributes (e.g. 'light', 'dark').
 * Themes are defined in _variables.scss via `[data-theme='...']` selectors.
 */
export function setTheme(themeName: string) {
  document.body.setAttribute('data-theme', themeName);
}

/** Remove the data-theme attribute to fall back to :root defaults (dark). */
export function resetTheme() {
  document.body.removeAttribute('data-theme');
}
